import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import queryString from "query-string";
import ErrorMessage from "../../shared/error/ErrorMessage";
import LoadingBox from "../../shared/LoadingBox";
import { usePaymentClient } from "../PaymentClient";
import { makeCancelable } from "../../utils";
import { LoadErrorPage } from "../../shared/error/LoadErrorPage";
import { MembershipProduct } from "../../contract/trial-membership/useMembershipProducts";
import { PaymentCustomerViewModel } from "../PaymentViewModel";

export interface ICustomerMembershipContext
  extends GetCustomerMembershipProductsResponseViewModel {
  customerGuid: string | null;
}

export interface GetCustomerMembershipProductsResponseViewModel {
  customer: PaymentCustomerViewModel;
  yearlyProducts: MembershipProduct[];
}

const CustomerMembershipContext =
  createContext<ICustomerMembershipContext | null>(null);

export default CustomerMembershipContext;

export const CustomerMembershipContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [isReadingQuery, setIsReadingQuery] = useState(true);
  const [customerGuid, setCustomerGuid] = useState<string | null>(null);
  const { fetch, loading, data } =
    usePaymentClient<GetCustomerMembershipProductsResponseViewModel>();

  useEffect(() => {
    if (customerGuid) {
      const cancelable = makeCancelable(
        fetch({
          path: `membership/customer/${customerGuid}/products`,
          method: "GET",
        })
      );

      cancelable.promise.catch((error) => {
        if (!error.isCanceled) throw error;
      });

      return () => cancelable.cancel();
    }
  }, [customerGuid, fetch]);

  useEffect(() => {
    const q = queryString.parse(document.location.search);

    setCustomerGuid(q.customer as string);
    setIsReadingQuery(false);
  }, []);

  useEffect(() => {
    const q = queryString.parse(document.location.search);

    window.history.replaceState(
      null,
      document.title,
      document.location.href.replace(
        document.location.search,
        "?" +
          queryString.stringify({
            ...q,
            customer: customerGuid,
          })
      )
    );
  }, [customerGuid]);

  if (isReadingQuery || loading || !data) return <LoadingBox />;

  if (!customerGuid)
    return (
      <LoadErrorPage>
        <ErrorMessage>Mangler kunde!</ErrorMessage>
      </LoadErrorPage>
    );

  return (
    <CustomerMembershipContext.Provider
      value={{
        customerGuid,
        ...data,
      }}
    >
      {children}
    </CustomerMembershipContext.Provider>
  );
};

export function useCustomerMembershipContext() {
  const context = useContext(CustomerMembershipContext);

  if (!context) throw new Error("No PaymentContext found!");

  return context;
}
